// Self-serve phone 4b-3: one Twilio Device app-wide + a floating dialer panel.
// Mounted once near the dashboard root. openDialer() prefills + shows the panel.

import { lazy, Suspense, useCallback, useState } from 'react'
// FRONTDOOR-SPEED-1 — by name, not the barrel. This provider wraps the whole
// app so it is in the entry chunk by design, and importing the barrel here
// pulled 598 KB of icons into every stranger's first load for ONE glyph.
import X from 'lucide-react/dist/esm/icons/x'
import { useVoiceDevice } from '../../hooks/useVoiceDevice'
// FRONTDOOR-SPEED-1 — LAZY. This provider wraps the whole app, so a static
// import put the entire dialer into the entry chunk every stranger downloads,
// for a panel that only renders once `open` is true. Measured: it was one of
// two eager paths from App.tsx to the 598 KB icon barrel.
const Dialer = lazy(() => import('../inbox/Dialer').then((m) => ({ default: m.Dialer })))
import { DialerContext, type DialerPrefill } from './voiceDialerContext'

export function VoiceDialerProvider({ children }: { children: React.ReactNode }) {
  const voice = useVoiceDevice()
  const [open, setOpen] = useState(false)
  const [prefill, setPrefill] = useState<DialerPrefill>({
    key: 0,
    number: '',
    contactType: null,
    contactId: null,
  })

  const openDialer = useCallback(
    (opts?: { number?: string; contactType?: string | null; contactId?: string | null }) => {
      setPrefill((p) => ({
        key: p.key + 1,
        number: opts?.number ?? '',
        contactType: opts?.contactType ?? null,
        contactId: opts?.contactId ?? null,
      }))
      setOpen(true)
    },
    [],
  )
  const closeDialer = useCallback(() => setOpen(false), [])

  const inCall = voice.callState !== 'idle'

  return (
    <DialerContext.Provider value={{ voice, prefill, open, openDialer, closeDialer }}>
      {children}
      {open && (
        // U3 (audit 5.7): on mobile the dialer sat at bottom-4 (16px) over the
        // 56px MobileBottomNav (both float; the dialer is z-50, nav z-40), and
        // its close is disabled during a call — so a rep on a live call could
        // neither dismiss it nor reach the nav. Lift it clear of the nav on
        // mobile (safe-area + 4.5rem, same shape as the create FAB) so the nav
        // is always reachable during a call. Desktop has no bottom nav, so it
        // keeps bottom-4 via the md: override. Call/hangup/audio untouched.
        <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] right-4 z-50 w-72 rounded-xl border border-border bg-card p-4 shadow-2xl md:bottom-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Dialer</p>
            <button
              type="button"
              onClick={closeDialer}
              disabled={inCall}
              title={inCall ? 'Hang up before closing' : 'Close'}
              className="text-subtle hover:text-foreground disabled:opacity-30"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Suspense fallback={null}>
            <Dialer />
          </Suspense>
        </div>
      )}
    </DialerContext.Provider>
  )
}
